import React from 'react';
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  List,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { NAVIGATION_STYLES } from '../../../config/constants';

/**
 * NavigationItem Component
 *
 * Renders a single navigation item with its icon, label, description, and optional sub-items.
 * Handles expansion/collapse for items with children and navigation for items without children.
 *
 * @param {Object} props - Component props
 * @param {Object} props.item - Navigation item configuration
 * @param {boolean} props.isActive - Whether this item is currently active
 * @param {boolean} props.isExpanded - Whether this item is expanded (if it has children)
 * @param {boolean} props.showDescription - Whether to show the description text
 * @param {Function} props.onItemClick - Callback for main item click
 * @param {Function} props.onSubItemClick - Callback for sub-item click
 * @param {Function} props.isSubItemActive - Function to check if a sub-item is active
 */
const NavigationItem = ({
  item,
  isActive,
  isExpanded,
  showDescription,
  onItemClick,
  onSubItemClick,
  isSubItemActive,
}) => {
  return (
    <React.Fragment>
      {/* Main Navigation Item */}
      <ListItem disablePadding>
        <ListItemButton
          onClick={() => onItemClick(item)}
          selected={isActive}
          sx={{
            borderRadius: 1,
            mb: 0.5,
            '&.Mui-selected': {
              backgroundColor: NAVIGATION_STYLES.colors.selectedBackground,
              '&:hover': {
                backgroundColor: NAVIGATION_STYLES.colors.selectedHover,
              },
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 40,
              color: isActive ? NAVIGATION_STYLES.colors.primary : 'inherit',
            }}
          >
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.label}
            secondary={showDescription ? item.description : null}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: isActive ? 600 : 400,
            }}
            secondaryTypographyProps={{
              fontSize: '0.75rem',
              color: 'text.secondary',
            }}
          />
          {item.children && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
        </ListItemButton>
      </ListItem>

      {/* Sub-items */}
      {item.children && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.children.map(subItem => (
              <ListItem key={subItem.id} disablePadding>
                <ListItemButton
                  onClick={() => onSubItemClick(subItem)}
                  selected={isSubItemActive(subItem.path)}
                  sx={{
                    pl: 4,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor:
                        NAVIGATION_STYLES.colors.subItemSelectedBackground,
                      '&:hover': {
                        backgroundColor:
                          NAVIGATION_STYLES.colors.subItemSelectedHover,
                      },
                    },
                  }}
                >
                  <ListItemText
                    primary={subItem.label}
                    primaryTypographyProps={{
                      fontSize: '0.8rem',
                      fontWeight: isSubItemActive(subItem.path) ? 500 : 400,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Collapse>
      )}
    </React.Fragment>
  );
};

export default NavigationItem;
